import React, { useState, useEffect } from 'react';
import { transactionAPI, paymentAPI } from '../../services/api';

const TransactionsTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, ...filters };
      const response = await transactionAPI.getAllTransactions(params);

      if (response.data.success) {
        setTransactions(response.data.data);
        setTotalPages(response.data.pages);
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async (transactionId) => {
    const reason = prompt('Enter refund reason:');
    if (reason) {
      try {
        const response = await transactionAPI.refundTransaction(transactionId, { reason });
        if (response.data.success) {
          loadTransactions();
          alert('Refund processed successfully');
        }
      } catch (err) {
        alert(err.response?.data?.error || 'Refund failed');
      }
    }
  };

  const handleMarkShipped = async (transactionId) => {
    const trackingNumber = prompt('Enter tracking number:');
    if (!trackingNumber) {
      return;
    }

    const carrier = prompt('Enter carrier (e.g., USPS, UPS, FedEx):');
    if (!carrier) {
      return;
    }

    try {
      await paymentAPI.markShipped(transactionId, { trackingNumber, carrier });
      loadTransactions();
      alert('Transaction marked as shipped successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark as shipped');
    }
  };

  const handleConfirmDelivery = async (transactionId) => {
    if (!window.confirm('Confirm that the item has been delivered?')) {
      return;
    }

    try {
      await paymentAPI.confirmDelivery(transactionId);
      loadTransactions();
      alert('Delivery confirmed! Payment released to seller.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to confirm delivery');
    }
  };

  const [retryingId, setRetryingId] = useState(null);
  const [payingId, setPayingId] = useState(null);

  const handleRetryPayout = async (transactionId) => {
    if (!window.confirm('Send the seller their payout now via Stripe? (Requires the seller to have connected Stripe.)')) {
      return;
    }
    try {
      setPayingId(transactionId);
      const response = await transactionAPI.retryPayout(transactionId);
      if (response.data.success) {
        loadTransactions();
        alert(response.data.message || 'Payout sent to the seller.');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send payout');
    } finally {
      setPayingId(null);
    }
  };

  const handleRetryLabel = async (transactionId) => {
    if (!window.confirm('Purchase a shipping label for this order now? This will charge your Shippo account.')) {
      return;
    }

    try {
      setRetryingId(transactionId);
      const response = await transactionAPI.retryShippingLabel(transactionId);
      if (response.data.success) {
        loadTransactions();
        const { trackingNumber, cost } = response.data.data || {};
        alert(`Shipping label purchased!${trackingNumber ? `\nTracking: ${trackingNumber}` : ''}${cost != null ? `\nLabel cost: $${Number(cost).toFixed(2)}` : ''}`);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to purchase shipping label');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: '24px' }}>Transactions Management</h1>

      {/* Error Alert */}
      {error && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          color: '#c00',
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Filters */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          style={{
            padding: '12px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {/* Transactions Table */}
      <div style={{ background: 'var(--cardBackground)', borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--background)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Item</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Platform Fee</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Buyer</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Seller</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: 'var(--textSecondary)' }}>
                    Loading transactions...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: 'var(--textSecondary)' }}>
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((txn) => (
                  <tr key={txn._id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px', fontSize: '12px', fontFamily: 'monospace' }}>
                      {txn._id.slice(-8)}
                    </td>
                    <td style={{ padding: '12px' }}>{txn.item?.title || 'Unknown'}</td>
                    <td style={{ padding: '12px', fontWeight: '600' }}>${(txn.amount / 100).toFixed(2)}</td>
                    <td style={{ padding: '12px', color: 'var(--success)' }}>${(txn.platformFee / 100).toFixed(2)}</td>
                    <td style={{ padding: '12px' }}>{txn.buyer?.username || 'Unknown'}</td>
                    <td style={{ padding: '12px' }}>{txn.seller?.username || 'Unknown'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: getStatusColor(txn.status),
                        color: 'white',
                      }}>
                        {txn.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--textSecondary)', fontSize: '14px' }}>
                      {new Date(txn.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {/* Pay Seller — order is released/completed but the payout
                            hasn't transferred yet (e.g., seller connected Stripe
                            after the sale, or an older pre-payout transaction) */}
                        {txn.status === 'completed' && txn.payoutStatus !== 'paid' && !txn.stripeTransferId && (
                          <button
                            onClick={() => handleRetryPayout(txn._id)}
                            disabled={payingId === txn._id}
                            style={{
                              padding: '8px 12px',
                              background: payingId === txn._id ? 'var(--secondary)' : '#0fa573',
                              color: payingId === txn._id ? 'var(--textSecondary)' : 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: payingId === txn._id ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                            }}
                            title="Send the seller their payout via Stripe"
                          >
                            {payingId === txn._id ? 'Paying…' : 'Pay Seller'}
                          </button>
                        )}
                        {txn.purchaseType === 'shipping' &&
                          !txn.shippingLabel?.labelUrl &&
                          txn.status !== 'refunded' &&
                          txn.status !== 'cancelled' && (
                          <button
                            onClick={() => handleRetryLabel(txn._id)}
                            disabled={retryingId === txn._id}
                            style={{
                              padding: '8px 12px',
                              background: retryingId === txn._id ? 'var(--secondary)' : '#9b59b6',
                              color: retryingId === txn._id ? 'var(--textSecondary)' : 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: retryingId === txn._id ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                            }}
                            title={txn.shippingLabelError
                              ? `Label failed: ${txn.shippingLabelError}. Top up Shippo, then retry.`
                              : 'Purchase the Shippo shipping label for this order'}
                          >
                            {retryingId === txn._id
                              ? 'Buying…'
                              : txn.shippingLabelError ? 'Retry Label' : 'Buy Label'}
                          </button>
                        )}
                        {txn.purchaseType === 'shipping' &&
                          !txn.shippingLabel?.labelUrl &&
                          txn.shippingLabelError && (
                          <span
                            style={{ color: 'var(--error)', fontSize: '12px', maxWidth: '160px' }}
                            title={txn.shippingLabelError}
                          >
                            ⚠️ Label failed
                          </span>
                        )}
                        {txn.purchaseType === 'shipping' && txn.shippingLabel?.labelUrl && (
                          <a
                            href={txn.shippingLabel.labelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '8px 12px',
                              background: '#2196F3',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '14px',
                              textDecoration: 'none',
                            }}
                            title="View/print the purchased shipping label"
                          >
                            Label
                          </a>
                        )}
                        {txn.status === 'paid' && (
                          <button
                            onClick={() => handleMarkShipped(txn._id)}
                            style={{
                              padding: '8px 12px',
                              background: 'var(--primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px',
                            }}
                          >
                            Ship
                          </button>
                        )}
                        {(txn.status === 'shipped' || txn.status === 'delivered') && (
                          <button
                            onClick={() => handleConfirmDelivery(txn._id)}
                            disabled={txn.status === 'delivered'}
                            style={{
                              padding: '8px 12px',
                              background: txn.status === 'delivered' ? 'var(--secondary)' : 'var(--success)',
                              color: txn.status === 'delivered' ? 'var(--textSecondary)' : 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: txn.status === 'delivered' ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                            }}
                          >
                            {txn.status === 'delivered' ? 'Delivered' : 'Confirm'}
                          </button>
                        )}
                        <button
                          onClick={() => handleRefund(txn._id)}
                          disabled={txn.status === 'refunded' || txn.status === 'completed'}
                          style={{
                            padding: '8px 12px',
                            background: (txn.status === 'refunded' || txn.status === 'completed') ? 'var(--secondary)' : 'var(--error)',
                            color: (txn.status === 'refunded' || txn.status === 'completed') ? 'var(--textSecondary)' : 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (txn.status === 'refunded' || txn.status === 'completed') ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                          }}
                        >
                          {txn.status === 'refunded' ? 'Refunded' : 'Refund'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', gap: '8px', borderTop: '1px solid var(--border)' }}>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{
                padding: '8px 16px',
                background: page === 1 ? 'var(--secondary)' : 'var(--primary)',
                color: page === 1 ? 'var(--textSecondary)' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Previous
            </button>
            <span style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              style={{
                padding: '8px 16px',
                background: page === totalPages ? 'var(--secondary)' : 'var(--primary)',
                color: page === totalPages ? 'var(--textSecondary)' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    pending: '#f5a623',
    paid: '#0fa573',
    shipped: '#0076d3',
    delivered: '#0fa573',
    completed: '#0fa573',
    cancelled: '#cc0000',
    refunded: '#f5a623',
  };
  return colors[status] || '#767676';
};

export default TransactionsTab;
